/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ButtonBase } from './ButtonBase';
import createTypography from '../utils/createTypography';

const buttonStylesMap = {
  base: [
    'sui-inline-flex',
    'sui-items-center',
    'sui-justify-center',
    'sui-relative',
    'sui-box-border',
    'sui-outline-none',
    'sui-outline-0',
    'sui-cursor-pointer',
    'sui-select-none',
    'sui-align-middle',
    'sui-appearance-none',
    'sui-no-underline',
    'sui-h-11'
  ].join(' '),
  primary: 'sui-min-w-16 sui-px-4 sui-rounded-base sui-border-0',
  secondary: 'sui-min-w-16 sui-bg-button-secondary sui-px-[14px] sui-rounded-base sui-border-solid sui-border-2',
  text: 'sui-w-fit sui-px-1 sui-font-normal sui-border-solid sui-border-b-2 sui-font-regular',
  active: {
    primary: 'sui-bg-button-primary sui-text-inverse',
    secondary: 'sui-text-brand sui-border-accent',
    text: 'sui-text-primary sui-border-active'
  },
  inactive: {
    primary: 'sui-bg-button-inactive-primary sui-text-inactive sui-pointer-events-none sui-cursor-default',
    secondary: 'sui-text-inactive sui-border-inactive sui-pointer-events-none sui-cursor-default',
    text: 'sui-text-inactive sui-border-inactive'
  },
  hover: {
    primary: 'hover:sui-bg-button-hover-primary hover:sui-text-inverse',
    secondary: 'hover:sui-bg-button-hover-secondary hover:sui-text-inverse hover:sui-border-0 hover:sui-px-4',
    text: 'hover:sui-border-accent'
  },
  focus: {
    primary: 'focus-visible:sui-bg-button-focus focus-visible:sui-text-primary',
    secondary: 'focus-visible:sui-bg-button-focus focus-visible:sui-text-primary focus-visible:sui-border-0 focus-visible:sui-px-4',
    text: 'focus-visible:sui-bg-button-focus focus-visible:sui-text-primary focus-visible:sui-border-focus'
  },
  loading: {
    base: 'sui-inline-flex sui-items-center !sui-text-transparent sui-pointer-events-none',
    active: {
      primary: '!sui-bg-button-primary',
      secondary: '!sui-bg-button-secondary !sui-border-2 !sui-border-accent'
    },
    inactive: {
      secondary: '!sui-bg-button-secondary sui-border-inactive'
    }
  }
};

const buttonStyles = {
  primary: ({ fullWidth, typography, disabled }) => classNames(`${buttonStylesMap.base} ${buttonStylesMap.primary} ${buttonStylesMap.focus.primary} ${typography}`, {
    'sui-w-full': fullWidth,
    'sui-w-full sm:sui-w-fit': !fullWidth,
    [`${buttonStylesMap.active.primary} ${buttonStylesMap.hover.primary}`]: !disabled,
    [`${buttonStylesMap.inactive.primary}`]: disabled
  }),
  secondary: ({ disabled, fullWidth, typography }) => classNames(`${buttonStylesMap.base} ${buttonStylesMap.secondary} ${buttonStylesMap.focus.secondary} ${typography}`, {
    'sui-w-full': fullWidth,
    'sui-w-full sm:sui-w-fit': !fullWidth,
    [`${buttonStylesMap.active.secondary} ${buttonStylesMap.hover.secondary}`]: !disabled,
    [`${buttonStylesMap.inactive.secondary}`]: disabled
  }),
  text: ({ disabled, typography }) => classNames(`${buttonStylesMap.base} ${buttonStylesMap.text} ${buttonStylesMap.text.secondary} ${buttonStylesMap.focus.text} ${typography}`, {
    [`${buttonStylesMap.active.text} ${buttonStylesMap.hover.text}`]: !disabled,
    [`${buttonStylesMap.inactive.text}`]: disabled
  })
};

const loadingButtonStyles = {
  primary: ({ buttonClasses, disabled }) => classNames(`${buttonClasses} ${buttonStylesMap.loading.base}`, {
    [`${buttonStylesMap.loading.active.primary}`]: !disabled
  }),
  secondary: ({ buttonClasses, disabled }) => classNames(`${buttonClasses} ${buttonStylesMap.loading.base}`, {
    [`${buttonStylesMap.loading.active.secondary}`]: !disabled,
    [`${buttonStylesMap.loading.inactive.secondary}`]: disabled
  }),
  text: ({ buttonClasses }) => classNames(`${buttonClasses} ${buttonStylesMap.loading.base}`),
};

const Button = React.forwardRef(({
  children,
  endIcon: EndIconComponent,
  startIcon: StartIconComponent,
  disabled = false,
  fullWidth = false,
  href,
  variant,
  loading,
  ...rest
}, ref) => {

  const isPrimary = variant === 'primary' || !variant;
  const isText = variant === 'text';

  const typography = createTypography({
    variant: 'body-base',
    weight: isText ? 'regular' : 'bold',
    height: 'normal',
    color: 'none'
  });

  const buttonClasses = buttonStyles[isPrimary ? 'primary' : variant]({
    disabled,
    fullWidth,
    typography: typography.classes
  });

  const classes = loading ? loadingButtonStyles[isPrimary ? 'primary' : variant]({ buttonClasses, disabled }) : buttonClasses;

  const startIcon = StartIconComponent && (
    <span className="sui-mr-2 sui--ml-1" style={{ display: 'inherit' }}>
      <StartIconComponent size="small" />
    </span>
  );

  const endIcon = EndIconComponent && (
    <span className="sui-ml-2 sui--mr-1" style={{ display: 'inherit' }}>
      <EndIconComponent size="small" />
    </span>
  );

  return (
    <>
      {href && (
        <a
          href={href}
          className={classes}
          {...rest}
        >
          {startIcon}
          {children}
          {endIcon}
        </a>
      )}
      {!href && (
        <ButtonBase disabled={disabled} ref={ref} unstyled className={classes} {...rest}>
          {startIcon}
          {children}
          {endIcon}
        </ButtonBase>
      )}
    </>
  );
});

Button.displayName = 'Button';

Button.propTypes = {
  /**
   * @ignore
   * Class override is for internal purposes only, will be removed shortly.
   * Use at your own risk.
   */
  children: PropTypes.node,
  /**
   * If `true`, the component is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * An icon to display after the text inside the button.
   */
  endIcon: PropTypes.any,
  /**
   * If `true`, the button will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * The URL to link to when the button is clicked.
   * If defined, an `a` element will be used as the root node.
   */
  href: PropTypes.string,
  /**
   * @ignore
   * If true applies the appropriate classes to convert it into a LoadingButton, for internal use only
   */
  loading: PropTypes.bool,
  /**
   * Callback fired when the component is clicked.
   */
  onClick: PropTypes.func,
  /**
   * An icon to display before the text inside the button.
   */
  startIcon: PropTypes.any,
  /**
   * The variant to use.
   * @default 'primary'
   */
  variant: PropTypes.oneOf(['primary', 'secondary', 'text']),
};

export { Button };
